import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { taskWorkflowConfigApi } from '../../services/taskWorkflowConfig';
import {
  GpuConfigInstallLog,
  TaskWorkflowConfig,
  TaskWorkflowConfigPayload,
} from '../../types';

interface TaskWorkflowConfigState {
  configs: TaskWorkflowConfig[];
  installLogs: GpuConfigInstallLog[];
  loading: boolean;
  saving: boolean;
  logsLoading: boolean;
  error: string | null;
}

const initialState: TaskWorkflowConfigState = {
  configs: [],
  installLogs: [],
  loading: false,
  saving: false,
  logsLoading: false,
  error: null,
};

export const fetchTaskWorkflowConfigs = createAsyncThunk(
  'taskWorkflowConfig/fetchAll',
  async (gpuType: string | undefined, { rejectWithValue }) => {
    try {
      const response = await taskWorkflowConfigApi.list(gpuType);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取任务 workflow 配置失败');
    }
  }
);

export const saveTaskWorkflowConfig = createAsyncThunk(
  'taskWorkflowConfig/save',
  async (payload: TaskWorkflowConfigPayload, { rejectWithValue }) => {
    try {
      const response = await taskWorkflowConfigApi.save(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '保存任务 workflow 配置失败');
    }
  }
);

export const disableTaskWorkflowConfig = createAsyncThunk(
  'taskWorkflowConfig/disable',
  async (id: number, { rejectWithValue }) => {
    try {
      await taskWorkflowConfigApi.disable(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || '禁用任务 workflow 配置失败');
    }
  }
);

export const fetchInstallLogs = createAsyncThunk(
  'taskWorkflowConfig/fetchInstallLogs',
  async (
    params: { gpuId?: string; taskType?: number } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await taskWorkflowConfigApi.installLogs(
        params?.gpuId,
        params?.taskType
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取安装日志失败');
    }
  }
);

const taskWorkflowConfigSlice = createSlice({
  name: 'taskWorkflowConfig',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskWorkflowConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTaskWorkflowConfigs.fulfilled,
        (state, action: PayloadAction<TaskWorkflowConfig[]>) => {
          state.loading = false;
          state.configs = action.payload;
        }
      )
      .addCase(fetchTaskWorkflowConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || '获取任务 workflow 配置失败';
      })
      .addCase(saveTaskWorkflowConfig.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        saveTaskWorkflowConfig.fulfilled,
        (state, action: PayloadAction<TaskWorkflowConfig>) => {
          state.saving = false;
          const index = state.configs.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index >= 0) {
            state.configs[index] = action.payload;
          } else {
            state.configs.unshift(action.payload);
          }
        }
      )
      .addCase(saveTaskWorkflowConfig.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || '保存任务 workflow 配置失败';
      })
      .addCase(
        disableTaskWorkflowConfig.fulfilled,
        (state, action: PayloadAction<number>) => {
          const index = state.configs.findIndex(
            (item) => item.id === action.payload
          );
          if (index >= 0) {
            state.configs[index] = { ...state.configs[index], enabled: false };
          }
        }
      )
      .addCase(disableTaskWorkflowConfig.rejected, (state, action) => {
        state.error = (action.payload as string) || '禁用任务 workflow 配置失败';
      })
      .addCase(fetchInstallLogs.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(
        fetchInstallLogs.fulfilled,
        (state, action: PayloadAction<GpuConfigInstallLog[]>) => {
          state.logsLoading = false;
          state.installLogs = action.payload;
        }
      )
      .addCase(fetchInstallLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = (action.payload as string) || '获取安装日志失败';
      });
  },
});

export default taskWorkflowConfigSlice.reducer;
